import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { GlobalNotificationProvider } from './services/GlobalNotificationService';
import { WelcomeScreen } from './components/WelcomeScreen';
import { CSENSELayout } from './components/CSENSELayout';
import { Dashboard } from './components/Dashboard';
import { History } from './components/History';
import { Alerts } from './components/Alerts';
import { SystemNotifications } from './components/SystemNotifications';
import { Settings } from './components/Settings';
import { CRM } from './components/CRM';
import { Profile } from './components/Profile';
import { Pricing } from './components/Pricing';
import { Auth } from './components/Auth';
import Cameras from './components/CamerasSimplified';
import { SimpleOrganizationSetup } from './components/SimpleOrganizationSetup';
import HikvisionManager from './components/HikvisionManager';
import { EdgeDeviceManagement } from './components/EdgeDeviceManagement';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Support } from './components/Support';
import { AboutUs } from './components/AboutUs';
import { Contact } from './components/Contact';
import { VehicleDetection } from './components/VehicleDetection';
import { AIAttendance } from './components/AIAttendance';
import { PeopleCount } from './components/PeopleCount';
import { Footfall } from './components/Footfall';
import { ParkingManagement } from './components/ParkingManagement';
import { Locations } from './components/Locations';
import { useNotifications } from './hooks/useNotifications';
import { setupFCMForUser, onForegroundMessage, testFCMNotification } from './config/firebase';
import SuspensionBanner from './components/SuspensionBanner';
import { useSuspensionCheck } from './hooks/useSuspensionCheck';
// Removed apiService import to avoid build issues

// Helper functions
const getAuthToken = () => localStorage.getItem('csense_auth_token');
const setAuthToken = (token: string) => localStorage.setItem('csense_auth_token', token);
const clearAuthToken = () => {
  localStorage.removeItem('csense_auth_token');
  localStorage.removeItem('token');
};
// Local API request function to avoid import issues
class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const makeApiRequest = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://csense.airivu.in';
  const token = localStorage.getItem('csense_auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.detail || `HTTP error! status: ${response.status}`, response.status);
  }

  return response.json();
};

const apiRequest = async (endpoint: string, options = {}) => {
  // Remove duplicate /api prefix if present
  const cleanEndpoint = endpoint.startsWith('/api/') ? endpoint.substring(4) : endpoint;
  return makeApiRequest(cleanEndpoint, options);
};

type DetectionType = 'fall-detection' | 'anpr' | 'fire-smoke';

interface Camera {
  id: string;
  name: string;
  streamUrl: string;
  detectionType: DetectionType;
  detectionActive?: boolean;
  location?: string;
  status?: 'online' | 'offline' | 'error';
  ip?: string;
  port?: number;
  username?: string;
  password?: string;
}

interface Alert {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: string;
  source: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  read: boolean;
  imageUrl?: string;
  alertType?: string;
}


export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [setupData, setSetupData] = useState<any>(null);
  const [systemAlerts, setSystemAlerts] = useState<any[]>([]);
  const [showAgentOverlay, setShowAgentOverlay] = useState(false);
  const backendUrl = import.meta.env.VITE_API_BASE_URL || 'https://csense.airivu.in';
  const [backendConnected] = useState(false);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loginGracePeriod, setLoginGracePeriod] = useState(false);
  const { isSuspended } = useSuspensionCheck();

  // Notification system
  const {
    notifications,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications();


  // Handle OAuth redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state && state.startsWith('google_oauth_popup')) {
      console.log('OAuth redirect detected:', code, state);
      if (window.opener) {
        window.opener.postMessage({
          type: 'GOOGLE_OAUTH_SUCCESS',
          code: code
        }, window.location.origin);
        window.close();
      } else {
        navigate('/login', { replace: true });
      }
    }
  }, [navigate]);

  // Load user cameras from backend when authenticated
  useEffect(() => {
    const loadUserCameras = async () => {
      if (isAuthenticated) {
        try {
          const response = await makeApiRequest('/api/cameras', {});
          const cameras = response.cameras || response || [];
          const backendCameras = cameras.map((cam: any) => ({
            id: cam._id || cam.id || 'unknown',
            name: cam.name || 'Unknown Camera',
            streamUrl: cam.rtsp_url || cam.streamUrl || '',
            detectionType: cam.detection_type || cam.detectionType || 'none',
            location: cam.location || '',
            status: 'online'
          }));
          setCameras(backendCameras);
          localStorage.setItem('csense-cameras', JSON.stringify(backendCameras));
          console.log('Loaded cameras from backend:', backendCameras);
        } catch (error) {
          console.error('Failed to load cameras from backend:', error);
          // Use mock data as fallback
          try {
            const { mockCameras } = await import('./utils/mockData');
            const formattedCameras = mockCameras.map((cam: any) => ({
              id: cam._id,
              name: cam.name,
              streamUrl: cam.rtsp_url,
              detectionType: cam.detection_type,
              location: cam.location,
              status: cam.status
            }));
            setCameras(formattedCameras);
            localStorage.setItem('csense-cameras', JSON.stringify(formattedCameras));
          } catch (mockError) {
            // Final fallback to localStorage
            const savedCameras = localStorage.getItem('csense-cameras');
            if (savedCameras) {
              setCameras(JSON.parse(savedCameras));
            }
          }
        }
      } else {
        // Load from localStorage for non-authenticated state
        const savedCameras = localStorage.getItem('csense-cameras');
        if (savedCameras) {
          setCameras(JSON.parse(savedCameras));
        }
      }
    };

    loadUserCameras();
  }, [isAuthenticated]);

  // Check authentication on mount and set up token refresh
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      // Try to get current user to validate token
      makeApiRequest('/api/auth/me', {})
        .then((userData: any) => {
          setUser(userData);
          setIsAuthenticated(true);
          setAuthLoading(false);
          // Don't redirect if already on setup page or authenticated pages
          if (!(userData.onboarding_completed || userData.onboardingCompleted) &&
            location.pathname !== '/setup' &&
            !['/dashboard', '/cameras', '/alerts', '/history', '/settings', '/profile', '/hikvision', '/vehicles', '/attendance', '/people-count', '/footfall', '/parking', '/locations', '/edge-devices'].includes(location.pathname)) {
            navigate('/setup', { replace: true });
          }
        })
        .catch((error: any) => {
          // Only clear token on actual auth failures (401/403), not network errors
          const status = error instanceof ApiError ? error.status : 0;
          if (status === 401 || status === 403) {
            clearAuthToken();
            setIsAuthenticated(false);
            setUser(null);
            if (!['/login', '/signup', '/', '/about', '/contact', '/pricing', '/support'].includes(location.pathname)) {
              navigate('/login', { replace: true });
            }
          }
          // On network errors (status 0) or server errors (5xx), stay logged in
          setAuthLoading(false);
        });
    } else {
      setAuthLoading(false);
    }
  }, []);

  // Real-time alert polling with token refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Set up token refresh every 25 minutes (before 30min expiry)
    const tokenRefreshInterval = setInterval(async () => {
      try {
        const response = await makeApiRequest('/api/auth/refresh', { method: 'POST' });
        setAuthToken(response.access_token);
      } catch (error) {
        console.error('Token refresh failed:', error);
        handleLogout();
      }
    }, 25 * 60 * 1000);

    return () => {
      clearInterval(tokenRefreshInterval);
    };
  }, [isAuthenticated]);

  const startDetectionForCamera = async (camera: Camera) => {
    try {
      console.log('🔄 Starting detection for camera:', camera);

      // Use the main coordinator for starting detection
      const response = await makeApiRequest('/api/detection/start', {
        method: 'POST',
        body: JSON.stringify({
          stream_url: camera.streamUrl,
          camera_id: camera.id,
          detection_type: camera.detectionType
        })
      });

      console.log('✅ Detection service started:', response);

      // Update camera status
      setCameras(prev =>
        prev.map(c =>
          c.id === camera.id
            ? { ...c, detectionActive: true, status: 'online' }
            : c
        )
      );

      // Update localStorage
      const updatedCameras = cameras.map(c =>
        c.id === camera.id
          ? { ...c, detectionActive: true, status: 'online' }
          : c
      );
      localStorage.setItem('csense-cameras', JSON.stringify(updatedCameras));

      addDynamicAlert({
        type: 'success',
        title: 'Detection Service Started',
        message: `${camera.detectionType} detection is now active for camera "${camera.name}"`,
        source: 'System',
        priority: 'low'
      });

    } catch (error) {
      console.error('❌ Failed to start detection:', error);

      // Update camera status to error
      setCameras(prev =>
        prev.map(c =>
          c.id === camera.id
            ? { ...c, detectionActive: false, status: 'error' }
            : c
        )
      );

      addDynamicAlert({
        type: 'error',
        title: 'Detection Service Failed',
        message: `Failed to start ${camera.detectionType} for camera "${camera.name}"`,
        source: 'System',
        priority: 'high'
      });
    }
  };

  const handleAuthSuccess = async (authData: any) => {
    console.log('Auth success:', authData);
    setUser(authData.user);
    setIsAuthenticated(true);
    
    // Start 60-second grace period - no notifications during this time
    setLoginGracePeriod(true);
    setTimeout(() => {
      setLoginGracePeriod(false);
      console.log('Login grace period ended - notifications now active');
    }, 60000);

    // Initialize FCM for logged-in user
    try {
      const fcmSetup = await setupFCMForUser();
      if (fcmSetup) {
        console.log('🔔 FCM initialized for user');
        
        // Setup foreground message handling
        onForegroundMessage((payload) => {
          console.log('📨 Foreground FCM message:', payload);
          
          // Add to app notifications
          addDynamicAlert({
            type: payload.data?.type || 'alert',
            title: payload.notification?.title || payload.data?.title || 'Security Alert',
            message: payload.notification?.body || payload.data?.message || 'New detection alert',
            source: 'FCM',
            priority: payload.data?.priority || 'high'
          });
          
          // Show browser notification if permission granted
          if (Notification.permission === 'granted') {
            new Notification(payload.notification?.title || '3RDI Alert', {
              body: payload.notification?.body || payload.data?.message,
              icon: '/favicon.ico',
              tag: payload.data?.alert_id || 'csense-alert'
            });
          }
        });
      }
    } catch (error) {
      console.error('❌ FCM setup failed:', error);
    }

    const from = location.state?.from?.pathname || '/dashboard';
    if (authData.user.onboarding_completed || authData.user.onboardingCompleted) {
      navigate(from, { replace: true });
    } else {
      navigate('/setup', { replace: true });
    }
  };

  type SetupAlert = {
    id: string;
    type: string;
    title: string;
    message: string;
    timestamp: string;
    source: string;
    priority: string;
    read: boolean;
    userDetails?: {
      name: any;
      email: any;
      phone: any;
      entity: any;
      entityType?: any;
      address?: any;
    };
  };

  const handleSetupComplete = async (data: any) => {
    console.log('🚀 Organization setup complete:', data);
    setSetupData(data);

    // Update user state to mark onboarding as completed
    if (user) {
      const updatedUser = {
        ...user,
        onboarding_completed: true,
        onboardingCompleted: true,
        organization_name: data.organization_name,
        organization_type: data.organization_type,
        address: data.address
      };
      setUser(updatedUser);
    }

    // Navigate to dashboard after setup
    navigate('/dashboard', { replace: true });

    addDynamicAlert({
      type: 'success',
      title: 'Welcome to 3RDI!',
      message: 'Organization setup complete. You can now add devices and cameras.',
      source: 'System',
      priority: 'low'
    });
  };

  const handleShowAuth = () => {
    navigate('/login');
  };

  const handleShowSupport = () => {
    navigate('/support');
  };

  const handleCloseSupport = () => {
    navigate(isAuthenticated ? '/dashboard' : '/');
  };

  const handleLogout = () => {
    console.log('Logout triggered');
    clearAuthToken();
    setIsAuthenticated(false);
    setUser(null);
    setSetupData(null);
    setSystemAlerts([]);
    
    // Clear FCM token on logout
    localStorage.removeItem('fcm_token');
    
    navigate('/', { replace: true });
  };

  const handleTabChange = (tab: string) => {
    console.log('Tab change requested:', tab);
    const routeMap: { [key: string]: string } = {
      'dashboard': '/dashboard',
      'cameras': '/cameras',
      'edge-devices': '/edge-devices',
      'hikvision': '/hikvision',
      'history': '/history',
      'crm': '/crm',
      'alerts': '/alerts',
      'settings': '/settings',
      'profile': '/profile',
      'support': '/support',
      'vehicles': '/vehicles',
      'attendance': '/attendance',
      'people-count': '/people-count',
      'footfall': '/footfall',
      'parking': '/parking',
      'locations': '/locations'
    };

    if (routeMap[tab]) {
      navigate(routeMap[tab]);
    }
  };

  // Detection management functions have been migrated to the service layer

  const fetchDetectionStats = async () => {
    try {
      const stats = await apiRequest('/api/stats');
      return stats;
    } catch (error) {
      console.error('Failed to fetch detection stats:', error);
    }
    return {
      total_frames: 0,
      alerts_sent: 0,
      active_streams: 0,
      uptime: "0"
    };
  };

  const fetchActiveStreams = async () => {
    try {
      const response = await apiRequest('/api/detection/active');
      return response.streams || [];
    } catch (error) {
      console.error('Failed to fetch active streams:', error);
    }
    return [];
  };



  // Detection alerts are now handled only in Alerts component



  const createToastNotification = (alert: Alert) => {
    // Create a toast div
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 z-50 max-w-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 transform transition-all duration-300 translate-x-full`;

    const iconColor = alert.type === 'error' ? 'text-red-500' :
      alert.type === 'warning' ? 'text-yellow-500' :
        'text-blue-500';

    toast.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <div class="w-5 h-5 ${iconColor}">
            ${alert.type === 'error' ? '⚠️' : alert.type === 'warning' ? '🔥' : '📸'}
          </div>
        </div>
        <div class="ml-3 flex-1">
          <p class="text-sm font-medium text-gray-900 dark:text-white">
            ${alert.title}
          </p>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            ${alert.message}
          </p>
          <div class="mt-2 flex space-x-2">
            <button onclick="window.app.viewAlert('${alert.id}')" class="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">
              View
            </button>
            <button onclick="this.closest('.fixed').remove()" class="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700">
              Dismiss
            </button>
          </div>
        </div>
        <button onclick="this.closest('.fixed').remove()" class="ml-auto flex-shrink-0 text-gray-400 hover:text-gray-600">
          ✕
        </button>
      </div>
    `;

    document.body.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);

    // Auto remove after 8 seconds
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => toast.remove(), 300);
    }, 8000);
  };

  const addDynamicAlert = (alertData: {
    type: string;
    title: string;
    message: string;
    source: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
  }) => {
    // Skip notifications during login grace period (except critical security alerts)
    if (loginGracePeriod && alertData.priority !== 'critical') {
      console.log('Skipping notification during login grace period:', alertData.title);
      return;
    }
    
    const newAlert: Alert = {
      ...alertData,
      id: `dynamic-${Date.now()}`,
      timestamp: new Date().toISOString(),
      read: false
    };
    setSystemAlerts(prev => [newAlert, ...prev]);

    // Fix the notification type issue
    addNotification({
      type: alertData.type === 'fall' ? 'alert' :
        alertData.type === 'fire' ? 'warning' : 'info',
      title: alertData.title,
      message: alertData.message,
      read: false,
      priority: alertData.priority === 'critical' ? 'high' : (alertData.priority || 'medium')
    });
  };

  // Test FCM function for debugging
  const handleTestFCM = async () => {
    try {
      const success = await testFCMNotification('Test FCM notification from frontend');
      if (success) {
        addDynamicAlert({
          type: 'success',
          title: 'FCM Test Sent',
          message: 'Test FCM notification has been sent to all your devices',
          source: 'System',
          priority: 'low'
        });
      }
    } catch (error) {
      console.error('FCM test failed:', error);
    }
  };

  // Expose test function globally for debugging
  (window as any).testFCM = handleTestFCM;

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    if (path === '/cameras') return 'cameras';
    if (path === '/edge-devices') return 'edge-devices';
    if (path === '/hikvision') return 'hikvision';
    if (path === '/history') return 'history';
    if (path === '/crm') return 'crm';
    if (path === '/alerts') return 'alerts';
    if (path === '/settings') return 'settings';
    if (path === '/profile') return 'profile';
    if (path === '/vehicles') return 'vehicles';
    if (path === '/attendance') return 'attendance';
    if (path === '/people-count') return 'people-count';
    if (path === '/footfall') return 'footfall';
    if (path === '/parking') return 'parking';
    if (path === '/locations') return 'locations';
    return 'dashboard';
  };

  const totalAlertCount = systemAlerts.filter(alert => !alert.read).length;

  // Add FCM test button in development
  const isDevelopment = import.meta.env.DEV;
  
  // Add keyboard shortcut for FCM test (Ctrl+Shift+F)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'F' && isAuthenticated) {
        handleTestFCM();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isAuthenticated]);

  // FCM navigation and alert refresh message listener
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'FCM_NAVIGATE') {
        console.log('🔔 FCM navigation request:', event.data.url);
        navigate(event.data.url);
      } else if (event.data?.type === 'FCM_ALERT_RECEIVED') {
        console.log('🔔 FCM alert received, triggering refresh');
        // Import and use alert service
        import('./services/AlertService').then(({ alertService }) => {
          alertService.refreshAlerts();
        });
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
  }, [navigate]);

  return (
    <GlobalNotificationProvider userEmail={isAuthenticated ? user?.email : null}>
      <SuspensionBanner isVisible={isSuspended && isAuthenticated} />
      <div className={isSuspended && isAuthenticated ? 'mt-16' : ''}>
        <Routes>
        <Route path="/" element={<WelcomeScreen onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} />} />
        <Route path="/about" element={<AboutUs onNavigate={(page) => navigate(page === 'home' ? '/' : `/${page}`)} onShowAuth={() => navigate('/login')} onShowSupport={() => navigate('/support')} />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/login" element={<Auth onAuthSuccess={handleAuthSuccess} onBack={() => navigate('/')} />} />
        <Route path="/support" element={<Support onClose={handleCloseSupport} />} />
        <Route path="/setup" element={
          authLoading ? (
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-white">Loading...</p>
              </div>
            </div>
          ) : isAuthenticated ? (
            <div className="min-h-screen bg-gray-950">
              <SimpleOrganizationSetup userData={user} onComplete={handleSetupComplete} />
            </div>
          ) : (
            <Navigate to="/login" state={{ from: location }} replace />
          )
        } />
        <Route path="/dashboard" element={
          authLoading ? (
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-white">Loading...</p>
              </div>
            </div>
          ) : (
            <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
              <div className="min-h-screen bg-gray-950">
                <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                  <Dashboard setupData={setupData} onAddAlert={addDynamicAlert} backendUrl={backendUrl} fetchDetectionStats={fetchDetectionStats} fetchActiveStreams={fetchActiveStreams} onNavigateToTab={handleTabChange} detectionActive={false} showAgentOverlay={false} onSetShowAgentOverlay={setShowAgentOverlay} />
                </CSENSELayout>
              </div>
            </ProtectedRoute>
          )
        } />
        <Route path="/cameras" element={
          authLoading ? (
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-white">Loading...</p>
              </div>
            </div>
          ) : isAuthenticated ? (
            user?.onboarding_completed || user?.onboardingCompleted ? (
              <div className="min-h-screen bg-gray-950">
                <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                  <Cameras />
                </CSENSELayout>
              </div>
            ) : (
              <Navigate to="/setup" replace />
            )
          ) : (
            <Navigate to="/login" state={{ from: location }} replace />
          )
        } />
        <Route path="/edge-devices" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <EdgeDeviceManagement />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/hikvision" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <HikvisionManager />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/history" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <History onAddAlert={addDynamicAlert} />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/vehicles" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <VehicleDetection />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/attendance" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <AIAttendance />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/people-count" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <PeopleCount />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/footfall" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <Footfall />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/parking" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <ParkingManagement />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/locations" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <Locations />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/crm" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <CRM />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/alerts" element={
          authLoading ? (
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-white">Loading...</p>
              </div>
            </div>
          ) : isAuthenticated ? (
            user?.onboarding_completed || user?.onboardingCompleted ? (
              <div className="min-h-screen bg-gray-950">
                <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                  <Alerts systemAlerts={systemAlerts} onUpdateAlerts={setSystemAlerts} onAddAlert={addDynamicAlert} />
                </CSENSELayout>
              </div>
            ) : (
              <Navigate to="/setup" replace />
            )
          ) : (
            <Navigate to="/login" state={{ from: location }} replace />
          )
        } />

        <Route path="/settings" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <Settings setupData={setupData} isSuperAdmin={user?.is_super_admin} />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute isAuthenticated={isAuthenticated} onboardingCompleted={user?.onboarding_completed || user?.onboardingCompleted}>
            <div className="min-h-screen bg-gray-950">
              <CSENSELayout activeTab={getActiveTab()} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} user={user} onShowAuth={handleShowAuth} onShowSupport={handleShowSupport} onLogout={handleLogout} alertCount={totalAlertCount} notifications={notifications} onMarkNotificationAsRead={markAsRead} onMarkAllNotificationsAsRead={markAllAsRead} onDeleteNotification={deleteNotification} showAgentOverlay={false}>
                <Profile onNavigateToTab={handleTabChange} onAddAlert={addDynamicAlert} />
              </CSENSELayout>
            </div>
          </ProtectedRoute>
        } />
        <Route path="*" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
        } />
        </Routes>
      </div>
    </GlobalNotificationProvider>
  );
}